import React, {
  useState,
  useEffect,
  useContext,
  PropsWithChildren,
} from 'react';
import agent from '../agent';
import useInterval from '../hooks/useInterval';
import types, { UserConversationGroupViewType } from '../types/services-api';
import { AxiosResponse } from 'axios';
import { isEmployerType, isStaffType, UserContext } from './UserContext';
import { getAuthToken } from '../utils/authentication';

export type ConversationsContextType = {
  conversationGroup: { [key: string]: types.UserConversationGroupViewType };
  fetchConversationGroup: (mostRecentThread?: boolean) => void;
  loadMoreConversations: (
    callback?: (group: UserConversationGroupViewType) => void,
    limit?: number
  ) => void;
  offsetInbox: number;
  isLoadingConversations: boolean;
  isLoadingMoreConversations: boolean;
  unreadMessages: number;
  updateUnreadMessages: (messageCount: number) => void;
  conversationFetchError: string;
  updateConversationGroup: (props: {
    jobId: number;
    username: string;
    values: Partial<types.UserConversationGroupViewType>;
  }) => void;
};

export const ConversationsContext =
  React.createContext<ConversationsContextType>({
    conversationGroup: {},
    fetchConversationGroup: () => {},
    loadMoreConversations: (
      callback?: (group: UserConversationGroupViewType) => void,
      limit?: number
    ) => {},
    offsetInbox: 0,
    isLoadingConversations: true,
    isLoadingMoreConversations: false,
    unreadMessages: 0,
    updateUnreadMessages: (messageCount) => {},
    conversationFetchError: '',
    updateConversationGroup: () => {},
  });

const INITIAL_INTERVAL_MS = 1000 * 10; // 10 seconds
const MAX_INTERVAL_MS = 1000 * 60 * 120; // 120 minutes
const PAGE_LIMIT = 30;

const ConversationsContextWrapper = (props: PropsWithChildren) => {
  const { user } = useContext(UserContext);
  const [isLoading, setIsLoading] = useState(true);
  const [conversationGroup, setConversationGroup] = useState<{
    [key: string]: UserConversationGroupViewType;
  }>({});
  const [changesetCheckIntervalMs, setChangesetCheckIntervalMs] =
    useState(INITIAL_INTERVAL_MS);
  const [offsetInbox, setOffsetInbox] = useState(0);
  const [isLoadingMoreConversations, setIsLoadingMoreConversations] =
    useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');

  const canAccessMessaging =
    isEmployerType(user) || (isStaffType(user) && !!user.position);

  useEffect(() => {
    if (canAccessMessaging) {
      manuallyCheckConversationsChangeset();
    } else if (Object.keys(conversationGroup).length > 0) {
      resetConversationsList();
    }
  }, [canAccessMessaging]);

  function fetchConversationGroup(mostRecentThread?: boolean) {
    // A changeset request still in flight at sign-out resolves after the
    // session is gone.
    if (!getAuthToken()) {
      return;
    }
    const isInitialLoad = Object.keys(conversationGroup).length === 0;
    if (isInitialLoad) {
      setIsLoading(true);
    }
    if (mostRecentThread) {
      agent.Conversations.getGroup({ limit: 1, offset: 0 })
        .then((res) => {
          setConversationGroup({
            ...conversationGroup,
            ...formatResponse(res),
          });
          setOffsetInbox(offsetInbox + threadsIn(res));
          setIsLoading(false);
        })
        .catch((err) => {
          setErrorMessage(err.message);
          setIsLoading(false);
        });
    } else {
      agent.Conversations.getGroup({ limit: PAGE_LIMIT, offset: 0 })
        .then((res) => {
          setConversationGroup(formatResponse(res));
          setOffsetInbox(threadsIn(res));
          setIsLoading(false);
        })
        .catch((err) => {
          setErrorMessage(err.message);
          setIsLoading(false);
        });
    }
  }

  function formatResponse(res: AxiosResponse<UserConversationGroupViewType[]>) {
    return res.data.reduce((prev, curr) => {
      return { ...prev, [`${curr.otherUsername}`]: curr };
    }, {});
  }

  // The API pages over conversation THREADS, and several threads with the same
  // employer arrive collapsed into one group. Advancing the offset by the group
  // count therefore under-counts what the server already returned, so the next
  // page re-fetches threads we have and older conversations stay unreachable.
  function threadsIn(res: AxiosResponse<UserConversationGroupViewType[]>) {
    return res.data.reduce((sum, group) => sum + group.threads.length, 0);
  }

  function manuallyCheckConversationsChangeset() {
    agent.Conversations.getChangeset()
      .then((res) => {
        updateUnreadMessages(res.data.totalUnreadCount);
        fetchConversationGroup();
      })
      .catch((err) => {
        console.warn(err.message);
      });
  }

  useInterval(
    () => {
      if (changesetCheckIntervalMs) {
        agent.Conversations.getChangeset()
          .then((res) => {
            if (
              res.data.totalUnreadCount === unreadMessages &&
              changesetCheckIntervalMs < MAX_INTERVAL_MS
            ) {
              // changeset didn't change, increase the interval
              const newIntervalMs = Math.round(
                changesetCheckIntervalMs + changesetCheckIntervalMs * 0.5
              );
              setChangesetCheckIntervalMs(newIntervalMs);
            } else {
              updateUnreadMessages(res.data.totalUnreadCount);
              fetchConversationGroup();
            }
          })
          .catch((err) => {
            console.warn(err.message);
          });
      }
    },
    canAccessMessaging ? changesetCheckIntervalMs : null
  );

  function resetConversationsList() {
    setConversationGroup({});
  }

  function loadMoreConversations(
    callback?: (group: UserConversationGroupViewType) => void,
    limit?: number
  ) {
    setIsLoadingMoreConversations(true);
    const requestBody = {
      limit: !!limit ? limit : PAGE_LIMIT,
      offset: offsetInbox,
    };
    agent.Conversations.getGroup(requestBody)
      .then((res) => {
        setIsLoadingMoreConversations(false);
        setConversationGroup({ ...conversationGroup, ...formatResponse(res) });
        if (res.data.length === 0) {
          setErrorMessage('No additional conversations available');
        }
        setOffsetInbox(offsetInbox + threadsIn(res));
        if (callback) {
          callback(res.data[0]);
        }
        return res;
      })
      .catch((err) => {
        setIsLoadingMoreConversations(false);
        setErrorMessage(err.message);
      });
  }

  function updateUnreadMessages(messageCount: number) {
    setUnreadMessages(messageCount);
    setChangesetCheckIntervalMs(INITIAL_INTERVAL_MS);
  }

  function updateConversationGroup(props: {
    jobId: number;
    username: string;
    values: Partial<types.UserConversationGroupViewType>;
  }) {
    const updatedGroup = Object.keys(conversationGroup).reduce(
      (obj, key) => {
        if (key === props.username) {
          return {
            ...obj,
            [key]: {
              ...conversationGroup[key],
              threads: [
                ...conversationGroup[key].threads.map((thread) => {
                  if (thread.jobId === props.jobId) {
                    return { ...thread, ...props.values };
                  } else {
                    return thread;
                  }
                }),
              ],
            },
          };
        } else {
          return { ...obj, [key]: conversationGroup[key] };
        }
      },
      {} as { [key: string]: UserConversationGroupViewType }
    );
    setConversationGroup(updatedGroup);
  }

  //for DevTools to display context name
  ConversationsContext.displayName = 'Conversations Context';

  return (
    <ConversationsContext.Provider
      value={{
        conversationGroup,
        fetchConversationGroup,
        updateConversationGroup,
        loadMoreConversations,
        offsetInbox,
        isLoadingConversations: isLoading,
        isLoadingMoreConversations,
        unreadMessages,
        updateUnreadMessages,
        conversationFetchError: errorMessage,
      }}
    >
      {props.children}
    </ConversationsContext.Provider>
  );
};

export default ConversationsContextWrapper;
